import { CheckCircle2, Camera, Calendar, Clock, MapPin, DollarSign, Star } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Booking } from "@/types";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";

export default function BookConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const [booking, setBooking] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);
  
  const bookingIdFromUrl = searchParams.get('booking_id');

  useEffect(() => {
    // Prevent multiple executions
    if (loadedRef.current) {
      return;
    }
    
    const loadBooking = async () => {
      try {
        // Check if we have bookingId from URL
        if (!bookingIdFromUrl) {
          console.error('No booking ID in URL');
          toast.error('Booking not found');
          navigate('/book/step-1');
          return;
        }

        loadedRef.current = true;
        
        // Get userId from localStorage (set after booking creation)
        const authSessionStr = localStorage.getItem('auth_session');
        let userId: string | null = null;
        let phone: string | null = null;
        
        if (authSessionStr) {
          try {
            const authSession = JSON.parse(authSessionStr);
            userId = authSession.userId || authSession.user?.id || null;
            phone = authSession.phone || authSession.user?.phone || null;
          } catch (e) {
            // Silently handle parsing errors
          }
        }

        // If no userId in localStorage, try to get from current user
        if (!userId && user?.id) {
          userId = user.id;
          phone = user.phone || phone;
        }

        if (!userId) {
          toast.error('You must be logged in to view this booking. Redirecting to login...');
          navigate('/auth/login');
          return;
        }

        // Fetch booking from database using edge function
        // Always fetch from DB - never use sessionStorage
        
        try {
          // Use helper to invoke with user's JWT token
          const { data: bookingResponse, error: fetchError } = await invokeFunctionWithAuth('get-booking-details', {
            bookingId: bookingIdFromUrl,
            userId: userId,
            phone: phone // Optional, for backward compatibility
          });

          if (fetchError) {
            throw fetchError;
          }

          if (bookingResponse?.booking) {
            setBooking(bookingResponse.booking);
            
            // Log filmmaker assignment info
            const loadedBooking = bookingResponse.booking;
            if (loadedBooking.filmmaker_id || loadedBooking.filmmaker) {
              console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
              console.log('🎬 FILMMAKER ATRIBUÍDO AO BOOKING:');
              console.log('   Filmmaker Nome:', loadedBooking.filmmaker?.full_name || 'N/A');
              console.log('   Status:', loadedBooking.status);
              if (loadedBooking.status === 'assigned') {
                console.log('   ⚠️ Filmmaker atribuído mas ainda não aceitou');
              } else if (loadedBooking.status === 'en_route') {
                console.log('   ✅ Filmmaker aceitou e está a caminho');
              }
              console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            } else {
              console.log('⚠️ Booking criado mas ainda sem filmmaker atribuído');
              console.log('   Status:', loadedBooking.status);
            }
            
            // Ensure user is authenticated in AuthContext
            if (!isAuthenticated && userId) {
              // AuthContext should pick this up from localStorage, but force refresh
              window.dispatchEvent(new StorageEvent('storage', {
                key: 'auth_session',
                newValue: authSessionStr
              }));
            }
          } else {
            throw new Error('Booking not found');
          }
        } catch (fetchError: any) {
          
          const errorMsg = fetchError?.message || 'Failed to load booking';
          if (errorMsg.includes('Unauthorized') || errorMsg.includes('401')) {
            toast.error('You do not have permission to view this booking.');
            navigate('/dashboard');
          } else {
            toast.error('Booking not found. Please check your bookings in the dashboard.');
            navigate('/dashboard');
          }
        }
      } catch (error: any) {
        toast.error('Error loading booking');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [bookingIdFromUrl, navigate]);

  // Poll for filmmaker assignment if booking doesn't have one yet
  useEffect(() => {
    if (!booking || booking.filmmaker_id) return;

    const pollForFilmmaker = async () => {
      if (!bookingIdFromUrl || !user?.id) return;

      try {
        const { data: bookingResponse } = await invokeFunctionWithAuth('get-booking-details', {
          bookingId: bookingIdFromUrl,
          userId: user.id,
          phone: user.phone
        });

        if (bookingResponse?.booking?.filmmaker_id) {
          const updatedBooking = bookingResponse.booking;
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('🎬 FILMMAKER FOI ATRIBUÍDO!');
          console.log('   Filmmaker Nome:', updatedBooking.filmmaker?.full_name || 'N/A');
          console.log('   Status:', updatedBooking.status);
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          setBooking(updatedBooking as any);
        }
      } catch (error) {
        // Silently fail - just continue polling
      }
    };

    // Poll every 2 seconds for up to 10 seconds
    const interval = setInterval(pollForFilmmaker, 2000);
    const timeout = setTimeout(() => clearInterval(interval), 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [booking, bookingIdFromUrl, user]);

  if (loading || !booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading confirmation...</p>
      </div>
    );
  }

  const bookingId = 'BK-' + booking.id.substring(0, 8).toUpperCase();
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatTime = (dateStr: string) => new Date(dateStr).toLocaleTimeString('en-US', { 
    timeZone: 'America/Toronto',
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  });
  const formatPrice = (cents: number) => `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const subtotalCents = booking.subtotal_cents ?? booking.total_cents ?? 0;
  const discountCents = booking.promo_code_discount_cents ?? 0;
  const serviceFeeCents = booking.package?.service_fee_cents ?? 0;
  const sameDayFeeCents = booking.now_flag ? (booking.package?.same_day_fee_cents ?? 0) : 0;
  const basePriceCents = booking.package?.base_price_cents ?? Math.max(subtotalCents - serviceFeeCents - sameDayFeeCents, 0);
  const totalCents = booking.total_cents ?? Math.max(subtotalCents - discountCents, 0);
  const promoCode = booking.promo_code_code;
  
  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3">
        <h1 className="text-lg font-semibold text-center">Booking Confirmed!</h1>
      </header>

      <div className="px-4 py-6 space-y-6 max-w-lg mx-auto">
        {/* Success Icon */}
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center">
            <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-primary-foreground" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Booking Confirmed</h2>
            <p className="text-muted-foreground">Your filming session has been successfully booked.</p>
          </div>
        </div>

        {/* Session Details */}
        <div className="space-y-4">
          <h3 className="font-bold text-lg">Session Details</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Camera className="h-5 w-5 text-primary" />
                <span>Package</span>
              </div>
              <span className="font-medium text-right">{booking.package?.name}</span>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-5 w-5 text-primary" />
                <span>Date</span>
              </div>
              <span className="font-medium text-right">
                {booking.now_flag ? 'Now (within 99 minutes)' : formatDate(booking.scheduled_at)}
              </span>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-5 w-5 text-primary" />
                <span>Time</span>
              </div>
              <span className="font-medium text-right">
                {booking.now_flag ? 'Immediate' : `${formatTime(booking.scheduled_at)} - ${booking.package?.duration_minutes ? formatTime(new Date(new Date(booking.scheduled_at).getTime() + booking.package.duration_minutes * 60000).toISOString()) : ''}`}
              </span>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-5 w-5 text-primary" />
                <span>Location</span>
              </div>
              <span className="font-medium text-right max-w-[60%]">
                {booking.address_line1}
                {booking.address_line2 && `, ${booking.address_line2}`}, {booking.city}, {booking.postal_code}
              </span>
            </div>

          </div>
        </div>

        {/* Order Summary */}
        <div className="space-y-4">
          <h3 className="font-bold text-lg">Order Summary</h3>
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Package Price</span>
              <span className="font-medium">{formatPrice(basePriceCents)}</span>
            </div>
            {sameDayFeeCents > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Same-Day Fee</span>
                <span className="font-medium">{formatPrice(sameDayFeeCents)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Service Fee</span>
              <span className="font-medium">{formatPrice(serviceFeeCents)}</span>
            </div>
            <div className="flex items-center justify-between text-sm pt-1">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">{formatPrice(subtotalCents)}</span>
            </div>
            {discountCents > 0 && (
              <div className="flex items-center justify-between text-sm text-emerald-600">
                <span className="font-medium">
                  Promo {promoCode ? `(${promoCode})` : ''}
                </span>
                <span className="font-semibold">- {formatPrice(discountCents)}</span>
              </div>
            )}
            <div className="border-t pt-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-5 w-5 text-primary" />
                <span className="font-semibold text-foreground">Total Paid</span>
              </div>
              <span className="font-bold text-lg">{formatPrice(totalCents)}</span>
            </div>
          </Card>
        </div>


        {/* What's Next */}
        <div className="space-y-4">
          <h3 className="font-bold text-lg">What's Next?</h3>
          
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary-foreground">1</span>
              </div>
              <div className="flex-1">
                <div className="font-semibold mb-1">Get ready for your session</div>
                <p className="text-sm text-muted-foreground">
                  Our filmmaker will arrive at your location at the scheduled time. Please ensure someone is available to grant access.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary-foreground">2</span>
              </div>
              <div className="flex-1">
                <div className="font-semibold mb-1">Keep your phone handy</div>
                <p className="text-sm text-muted-foreground">
                  We'll send you SMS updates about your filmmaker's arrival and progress throughout the session.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary-foreground">3</span>
              </div>
              <div className="flex-1">
                <div className="font-semibold mb-1">Identity verification</div>
                <p className="text-sm text-muted-foreground">
                  Upon arrival, the filmmaker will provide you with a 6-digit code. Enter it in the app to confirm their identity and start the session timer.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="space-y-3 pt-4">
          <Link 
            to={`/track/${bookingId}`} 
            onClick={(e) => {
              e.preventDefault();
              sessionStorage.removeItem('confirmedBooking');
              sessionStorage.removeItem('bookingFormData');
              // Replace current history entry with dashboard so browser back from track goes to dashboard
              window.history.replaceState(null, '', '/dashboard');
              // Navigate to track with push so we can go back to dashboard
              navigate(`/track/${bookingId}`);
            }}
          >
            <Button className="w-full h-14 text-base font-bold rounded-xl shadow-lg">
              Track Your Booking
            </Button>
          </Link>
          <Link to="/" onClick={() => {
            sessionStorage.removeItem('confirmedBooking');
            sessionStorage.removeItem('bookingFormData');
          }}>
            <Button variant="ghost" className="w-full h-12 text-base font-semibold">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
